import { isTodoActionCard } from '@littlebird/action-cards';
import { useStores, type ActionCardPlacement } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { Markdown } from '@/components/ui/smoothdown';
import {
  DelegateCardButton,
  ReceivedDelegationLine,
  useCanDelegateCard,
  useDelegatedSourceCardIds,
} from '@/features/action-card-delegation';
import {
  CardFallback,
  detachCard,
  FeedCard,
  FeedSubtaskList,
  reportedSurfaceOf,
  useCardRendering,
  type FeedCardProps,
} from '@/features/action-cards-view';
import { MARKDOWN_CLASS } from '@/features/action-cards-view/registry/tokens';
import { CodingAgentCard } from '@/features/action-cards/components/CodingAgentCard';
import { AskUserActionCardBody } from '@/features/ask-user/AskUserActionCardBody';
import { FEATURES, hasFeature } from '@/features/feature-flags';
import {
  HivemindGroupContent,
  HivemindGroupIcon,
} from '@/features/hivemind/conversations/hivemind-group-content';
import { useHivemindGroup } from '@/features/hivemind/conversations/use-hivemind-group';
import { useHivemindRowTitle } from '@/features/hivemind/conversations/use-hivemind-row-title';
import { useIsInternalUser } from '@/hooks/useIsInternalUser';

import { entryLastActiveAt } from '../need-you-cards';

import { DetachFromParentButton } from './DetachFromParentButton';
import { feedCardProvider, feedCheckIcon } from './feedCardIcon';

import type {
  PendingActionCardEntry,
  WaitingForYouState,
} from '../use-waiting-for-you';
type HomeCardProps = {
  readonly entry: PendingActionCardEntry;
  readonly home: WaitingForYouState;
  readonly surface: 'web_home' | 'web_feeds';
  readonly placement?: ActionCardPlacement;
  readonly onOpen?: () => void;
  readonly keyboardTarget?: boolean;
  readonly leavesWhenDone?: boolean;
  // Called just before the card leaves the list, by a dismissal or a run behind a toast
  readonly onLeave?: () => void;
};

type MetadataRows = readonly (readonly [string, unknown])[];

function InternalCardMetadataHover({
  children,
  cardRows,
  sourceRows,
  jevRows,
  bootstrapRows,
}: {
  readonly children: ReactNode;
  readonly cardRows: MetadataRows;
  readonly sourceRows: MetadataRows;
  readonly jevRows: MetadataRows;
  readonly bootstrapRows: MetadataRows;
}) {
  const [metadataOpen, setMetadataOpen] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setMetadataOpen(true)}
      onMouseLeave={() => setMetadataOpen(false)}
    >
      {children}
      {metadataOpen &&
        createPortal(
          <aside className="fixed right-4 top-20 z-50 w-80 max-h-[calc(100vh-6rem)] overflow-y-auto rounded-lg border border-border bg-background p-3 text-xs shadow-lg">
            <section className="space-y-1">
              <div className="font-semibold">Card</div>
              {cardRows.map(([label, value]) => (
                <div key={label} className="break-words">
                  {label}: {String(value)}
                </div>
              ))}
            </section>
            {sourceRows.length > 0 && (
              <section className="mt-3 space-y-1">
                <div className="font-semibold">Source</div>
                {sourceRows.map(([label, value]) => (
                  <div key={label} className="break-words">
                    {label}: {String(value)}
                  </div>
                ))}
              </section>
            )}
            {jevRows.length > 0 && (
              <section className="mt-3 space-y-1">
                <div className="font-semibold">Jev</div>
                {jevRows.map(([key, value]) => (
                  <div key={key} className="break-words">
                    {key}: {String(value)}
                  </div>
                ))}
              </section>
            )}
            {bootstrapRows.length > 0 && (
              <section className="mt-3 space-y-1">
                <div className="font-semibold">Bootstrap</div>
                {bootstrapRows.map(([label, value]) => (
                  <div key={label} className="break-words">
                    {label}: {String(value)}
                  </div>
                ))}
              </section>
            )}
          </aside>,
          document.body
        )}
    </div>
  );
}

export const HomeCard = observer(
  ({
    entry,
    home,
    surface,
    placement,
    onOpen,
    keyboardTarget,
    leavesWhenDone,
    onLeave,
  }: HomeCardProps) => {
    // Views arrive by sync on the same MobX card, so React Compiler must not cache this
    'use no memo';
    const { card, decoded } = entry;
    const { reports, routines } = useStores();
    const isInternalUser = useIsInternalUser();
    const showMetadataHover =
      isInternalUser && hasFeature(FEATURES.FEED_CARD_DEBUG_PANEL);
    const report = card.reportId
      ? reports.getByReferenceId(card.reportId)
      : undefined;
    const runContext = report?.runContext;
    const routine = report
      ? routines.getByReferenceId(report.reportTemplateId)
      : undefined;
    const runStartedAt = runContext?.['run-started-at'];
    const sourceRows = (
      [
        ['Routine', routine?.title],
        ['Trigger', runContext?.['trigger-type']],
        [
          'Query / endpoint',
          runContext?.['trigger-query'] ?? runContext?.['trigger-endpoint'],
        ],
        [
          'Run started',
          typeof runStartedAt === 'string'
            ? new Date(runStartedAt).toLocaleString()
            : runStartedAt,
        ],
        ['Source message', runContext?.['source-message-id']],
      ] as const
    ).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    );
    const jevRows = Object.entries(runContext ?? {}).filter(
      ([key, value]) =>
        key.startsWith('jev-') &&
        value !== undefined &&
        value !== null &&
        value !== ''
    );
    const bootstrapRows = (
      [
        ['Tag', card.bootstrapDecision?.tag],
        ['Category', card.bootstrapDecision?.category],
        ['Relationship', card.bootstrapDecision?.relationship],
        ['Confidence', card.bootstrapDecision?.confidence],
        ['Reason', card.bootstrapDecision?.reason],
        ['Protected', card.bootstrapDecision?.protected],
        ['Archive candidate', card.bootstrapDecision?.archive_candidate],
      ] as const
    ).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    );
    const cardRows = [
      ['Importance', card.importance ?? 'unrated'],
      ...(card.dueAt
        ? [
            [
              'Due',
              `${card.dueAt.toLocaleString()}${card.dueAtEstimated ? ' (estimated)' : ''}`,
            ] as const,
          ]
        : []),
      ...(card.expiresAt
        ? [['Expires', card.expiresAt.toLocaleString()] as const]
        : []),
    ] as const;
    const open = home.isItemExpanded(card.id);
    // An outgoing message is one card, to one person or several, whose row and body say so
    const hivemindGroup = useHivemindGroup(card);
    const group = hivemindGroup?.isFirst ? hivemindGroup : null;
    const replyTitle = useHivemindRowTitle(card);
    const provenance = home.getProvenance(card);
    const { openSource } = provenance;
    // The row's title is the group's status, so its status line says only how the message went out
    const sentCount =
      group?.cards.filter(({ status }) => status === 'completed').length ?? 0;
    const sentLabel =
      sentCount > 1
        ? `Sent to ${sentCount} people`
        : sentCount === 1 && group?.rows.length === 1
          ? group.sentAutomatically
            ? 'Sent automatically'
            : 'Sent'
          : undefined;
    const body: FeedCardProps['body'] = group
      ? (header) => (
          <>
            {header(group.title)}
            <HivemindGroupContent
              group={group}
              clampAnswers
              onOpenChat={openSource}
            />
          </>
        )
      : decoded.content.type === 'ask_user'
        ? (header) => (
            <AskUserActionCardBody
              card={card}
              renderHeader={header}
              context={
                card.context ? (
                  <Markdown
                    id={`ask-user-context-${card.id}`}
                    content={card.context}
                    className={MARKDOWN_CLASS}
                  />
                ) : undefined
              }
              autoFocus={open}
            />
          )
        : undefined;
    const delegated = useDelegatedSourceCardIds().has(card.id);
    const canAssign = useCanDelegateCard(card) && !delegated;
    const rendering = useCardRendering(card, surface);
    const showsLoading = body === undefined && rendering === 'loading';
    const showsFallback = body === undefined && rendering === 'fallback';
    if (card.actionType === 'coding_agent')
      return <CodingAgentCard card={card} />;
    const { openInCardChat } = home.followUp;
    const subject = decoded.summary || decoded.actionLabel;
    // Where the host slides a done card out, a run leaves the list at once and goes on behind a toast
    const inBackground = !!leavesWhenDone && home.runsInBackground(card.id);
    // A card that leaves while open hands the open place to the next card; one that leaves from its collapsed row
    // leaves the next one collapsed
    const leave = open ? onLeave : undefined;
    // The open frame dismisses through this state, so it tells the host too
    const deferredDismiss = leave
      ? {
          ...home.deferredDismiss,
          beginDismiss: (
            ...args: Parameters<typeof home.deferredDismiss.beginDismiss>
          ) => {
            leave();
            home.deferredDismiss.beginDismiss(...args);
          },
        }
      : home.deferredDismiss;
    const { children } = entry;
    const familyOpen = home.isFamilyExpanded(card.id);
    const subtaskOpen = !!children?.open.some(({ card: child }) =>
      home.isFamilyExpanded(child.id)
    );
    // Drawn even when the card itself cannot be, since its subtasks are left out of the lists
    const childrenBlock = children ? (
      <FeedSubtaskList
        subtasks={children}
        subtaskOpen={subtaskOpen}
        onExpand={() => {
          if (!familyOpen) (onOpen ?? (() => home.openOnly(card.id)))();
        }}
        onCollapse={() => {
          if (!subtaskOpen) return;
          children.open.forEach(({ card: child }) =>
            home.setItemExpanded(child.id, false)
          );
          home.setItemExpanded(card.id, true);
        }}
        renderRow={(child) => (
          <HomeCard
            entry={child}
            home={home}
            surface={surface}
            leavesWhenDone={leavesWhenDone}
            keyboardTarget={home.isItemExpanded(child.card.id)}
            onOpen={() =>
              home.openOnly(
                home.isItemExpanded(child.card.id) ? card.id : child.card.id
              )
            }
          />
        )}
      />
    ) : null;
    const self = showsLoading ? null : showsFallback ? (
      <CardFallback card={card} surface={surface} />
    ) : (
      <>
        <FeedCard
          card={card}
          body={body}
          title={group?.title ?? replyTitle}
          preview={group?.text}
          outcomeLabel={sentLabel}
          surface={surface}
          placement={placement}
          icon={
            group ? (
              <HivemindGroupIcon group={group} />
            ) : (
              feedCheckIcon(entry, provenance)
            )
          }
          todo={isTodoActionCard(decoded.content)}
          appName={feedCardProvider(entry).label}
          open={open}
          keepOpen={subtaskOpen}
          onToggle={
            onOpen ?? (() => home.openOnly(familyOpen ? undefined : card.id))
          }
          deferredDismiss={deferredDismiss}
          onDismiss={() => {
            leave?.();
            home.deferDismiss([entry], undefined, reportedSurfaceOf(surface));
          }}
          onRetain={() => {
            if (!inBackground) {
              home.retainCards([card.id]);
              return;
            }
            leave?.();
            home.launchCard(card);
          }}
          onRunEnd={
            inBackground ? (end) => home.landCard(card, end) : undefined
          }
          onOpenCardChat={() => home.followUp.attach(card)}
          onOpenInCardChat={
            openInCardChat && ((seed) => openInCardChat(card, seed))
          }
          onOpenSource={openSource}
          meetingSource={provenance.meeting}
          keyboardTarget={keyboardTarget}
          subtasks={children?.progress}
          lastActiveAt={new Date(entryLastActiveAt(entry)).toISOString()}
          leavesWhenDone={leavesWhenDone}
          rowAssign={
            canAssign
              ? (trigger) => (
                  <DelegateCardButton
                    card={card}
                    subject={subject}
                    trigger={trigger}
                    surface={reportedSurfaceOf(surface)}
                  />
                )
              : undefined
          }
          actions={
            card.parentId ? (
              <DetachFromParentButton
                onDetach={() =>
                  void detachCard(card, reportedSurfaceOf(surface))
                }
              />
            ) : null
          }
        />
        <ReceivedDelegationLine card={card} className="px-5 pb-2 text-xs" />
      </>
    );
    return (
      <>
        {showMetadataHover ? (
          <InternalCardMetadataHover
            cardRows={cardRows}
            sourceRows={sourceRows}
            jevRows={jevRows}
            bootstrapRows={bootstrapRows}
          >
            {self}
          </InternalCardMetadataHover>
        ) : (
          self
        )}
        {childrenBlock}
      </>
    );
  }
);
